// "My Notes" scratchpad store (module singleton, task-counts-store shape). The same file
// is loaded by BOTH windows' bundles — each JS context gets its own independent instance:
// the MAIN window keeps it warm for the titlebar/sidebar badges and refreshes on the WS
// ping; the NOTES window renders from it and refreshes on the IPC ping + visibility.
//
// Owner-stamped like every per-user store: a PIN profile switch drops the cached blocks
// immediately, and in-flight responses that started under a different user are discarded.
// Mutations are optimistic with rollback; every action resolves { ok, error } and never
// throws, so callers only decide what to toast.

import {
  createUserNoteBlock,
  deleteUserNoteBlock,
  fetchUserNotes,
  updateUserNoteBlock,
  uploadUserNoteFile,
} from '@/lib/api/user-notes';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { subscribeUserNotesEvents } from '@/lib/realtime/chat-realtime-service';
import { countOpenTodos, NOTES_MAX_FILE_BYTES } from '@/lib/notes/notes-helpers';

const WS_DEBOUNCE_MS = 400;

let blocks = null; // null until the first successful fetch for the current owner
let saveError = false;
let pendingMutations = 0;
let started = false;
let wsDebounceTimer = null;
let notesOwnerUserId = '';
let inFlight = null;
let inFlightUserId = '';
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      /* ignore listener failures */
    }
  }
}

function currentUserId() {
  return String(getAuthUser()?.id || '').trim();
}

function dropNotesIfOwnerChanged(userId) {
  if (blocks === null || notesOwnerUserId === userId) return;
  blocks = null;
  notesOwnerUserId = '';
  saveError = false;
  emit();
}

/** Fetch the full list (coalesced: concurrent calls share one request per user). */
export async function refreshUserNotes() {
  if (!getAuthToken()) {
    if (blocks !== null) {
      blocks = null;
      notesOwnerUserId = '';
      saveError = false;
      emit();
    }
    return null;
  }
  const requestUserId = currentUserId();
  dropNotesIfOwnerChanged(requestUserId);
  if (inFlight && inFlightUserId === requestUserId) {
    return inFlight;
  }
  inFlightUserId = requestUserId;
  inFlight = (async () => {
    try {
      const next = await fetchUserNotes();
      // Identity guard: a profile switch mid-flight must not land user A's private notes
      // in user B's window.
      if (currentUserId() !== requestUserId) return blocks;
      blocks = next;
      notesOwnerUserId = requestUserId;
      saveError = false;
      emit();
    } catch {
      /* keep the previous (same-user) blocks; the next trigger retries */
    } finally {
      inFlight = null;
      inFlightUserId = '';
    }
    return blocks;
  })();
  return inFlight;
}

function scheduleWsRefresh() {
  if (wsDebounceTimer) return;
  wsDebounceTimer = setTimeout(() => {
    wsDebounceTimer = null;
    void refreshUserNotes();
  }, WS_DEBOUNCE_MS);
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshUserNotes();
  // Never unsubscribed — the store lives for the window session, like the other stores.
  subscribeAuthStateChange(() => void refreshUserNotes());
  // Fires only in the window that runs the chat socket (the main window); harmless in the
  // notes window, which is pinged over IPC instead.
  subscribeUserNotesEvents(() => scheduleWsRefresh());
}

/**
 * Runs one optimistic mutation: apply the local patch, await the API call, reconcile or
 * roll back. Owner changes mid-flight abandon the reconcile (the drop already cleared us).
 */
async function runMutation({ apply, call, reconcile }) {
  const requestUserId = currentUserId();
  const before = blocks;
  if (typeof apply === 'function' && blocks !== null) {
    blocks = apply(blocks);
  }
  pendingMutations += 1;
  emit();
  try {
    const result = await call();
    if (currentUserId() === requestUserId && typeof reconcile === 'function' && blocks !== null) {
      blocks = reconcile(blocks, result);
    }
    saveError = false;
    return { ok: true, error: null };
  } catch (error) {
    if (currentUserId() === requestUserId) {
      blocks = before;
      saveError = true;
    }
    return { ok: false, error };
  } finally {
    pendingMutations -= 1;
    emit();
  }
}

function replaceBlock(list, block) {
  if (!block) return list;
  return list.map((existing) => (existing.id === block.id ? block : existing));
}

/** Save a plain note. The stream shows it once the server block lands (prepended). */
export async function createNoteBlockAction(text) {
  return runMutation({
    call: () => createUserNoteBlock({ type: 'note', text }),
    reconcile: (list, created) => (created ? [created, ...list] : list),
  });
}

/** Save a checklist ({id,label,done?} rows, stored order). */
export async function createTodosBlockAction(items) {
  return runMutation({
    call: () => createUserNoteBlock({ type: 'todos', items }),
    reconcile: (list, created) => (created ? [created, ...list] : list),
  });
}

/** Check/uncheck one todo item — optimistic flip, PUT of the full stored-order list. */
export async function toggleTodoItemAction(blockId, itemId, done) {
  const block = (blocks || []).find((b) => b.id === blockId);
  if (!block || block.type !== 'todos') return { ok: false, error: null };
  const nextItems = block.items.map((item) =>
    item.id === itemId
      ? { ...item, done, doneAt: done ? new Date().toISOString() : null }
      : item,
  );
  return runMutation({
    apply: (list) => replaceBlock(list, { ...block, items: nextItems }),
    call: () => updateUserNoteBlock({ blockId, items: nextItems }),
    reconcile: (list, updated) => replaceBlock(list, updated),
  });
}

/** Delete one block (optimistic removal, rollback on failure). */
export async function deleteBlockAction(blockId) {
  return runMutation({
    apply: (list) => list.filter((block) => block.id !== blockId),
    call: () => deleteUserNoteBlock({ blockId }),
  });
}

/**
 * Upload one dropped/pasted/picked file (≤25 MB — oversized files resolve
 * { ok: false, reason: 'too-large' } without touching the network).
 */
export async function uploadNoteFileAction(file) {
  if (!(file instanceof Blob)) return { ok: false, error: null };
  if (file.size > NOTES_MAX_FILE_BYTES) {
    return { ok: false, error: null, reason: 'too-large' };
  }
  return runMutation({
    call: () => uploadUserNoteFile({ file }),
    reconcile: (list, created) => (created ? [created, ...list] : list),
  });
}

/** ⋯ menu "Clear completed": strip done items everywhere; fully-done blocks are deleted. */
export async function clearCompletedTodosAction() {
  const todosBlocks = (blocks || []).filter(
    (block) => block.type === 'todos' && block.items.some((item) => item.done),
  );
  let failed = false;
  for (const block of todosBlocks) {
    const open = block.items.filter((item) => !item.done);
    const result = open.length
      ? await runMutation({
          apply: (list) => replaceBlock(list, { ...block, items: open }),
          call: () => updateUserNoteBlock({ blockId: block.id, items: open }),
          reconcile: (list, updated) => replaceBlock(list, updated),
        })
      : await deleteBlockAction(block.id);
    if (!result.ok) failed = true;
  }
  return { ok: !failed, cleared: todosBlocks.length };
}

/** Snapshot for rendering: { blocks (null until loaded), saveState }. */
export function getUserNotesState() {
  ensureStarted();
  dropNotesIfOwnerChanged(currentUserId());
  let saveState = 'saved';
  if (saveError) saveState = 'error';
  else if (pendingMutations > 0) saveState = 'saving';
  return { blocks, saveState };
}

/** Open to-do count for the titlebar pill / sidebar badge (0 while logged out/unloaded). */
export function getNotesOpenTodoCount() {
  ensureStarted();
  dropNotesIfOwnerChanged(currentUserId());
  return countOpenTodos(blocks);
}

export function subscribeUserNotes(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Badge-shaped subscription: called with the current open-todo count on every change. */
export function subscribeNotesOpenTodoCount(listener) {
  if (typeof listener !== 'function') return () => {};
  return subscribeUserNotes(() => listener(countOpenTodos(blocks)));
}
